import React, { useEffect, useState } from "react";
import { Share2, Copy, Check, Facebook, Twitter, Linkedin, Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const shareUrlFor = (kind, slug) => `${API_BASE}/api/share/${kind}/${slug}`;

export const useSeo = ({ title, description, image, kind, slug, item }) => {
  useEffect(() => {
    if (!item) return;
    const prev = document.title;
    document.title = `${title} | HeyGC`;
    const set = (sel, attr, val) => {
      let el = document.head.querySelector(sel);
      if (!el) { el = document.createElement("meta"); const [k, v] = sel.replace("meta[", "").replace("]", "").split("="); el.setAttribute(k, v.replace(/"/g, "")); document.head.appendChild(el); }
      el.setAttribute(attr, val);
    };
    set('meta[property="og:title"]', "content", title);
    set('meta[name="description"]', "content", description || "");
    set('meta[property="og:description"]', "content", description || "");
    if (image) set('meta[property="og:image"]', "content", image);
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-seo", kind);
    api.get(`/share/${kind}/${slug}/jsonld`).then((r) => { script.textContent = JSON.stringify(r.data); document.head.appendChild(script); }).catch(() => {});
    return () => { document.title = prev; script.remove(); };
  }, [item, title, description, image, kind, slug]);
};

const NETWORKS = (url, title) => [
  { k: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}` },
  { k: "x", label: "X / Twitter", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}` },
  { k: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${encodeURIComponent(`${title} — ${url}`)}` },
  { k: "linkedin", label: "LinkedIn", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}` },
  { k: "email", label: "Email", icon: Mail, href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`Thought you'd like this on HeyGC:\n${url}`)}` },
];

export const ShareButton = ({ kind, slug, title, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = shareUrlFor(kind, slug);

  const share = async () => {
    if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
      try { await navigator.share({ title, url }); return; } catch { /* user cancelled or unsupported — fall through to dialog */ }
    }
    setOpen(true);
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied."); setTimeout(() => setCopied(false), 1500); }
    catch { toast.error("Couldn't copy — long-press the link instead."); }
  };

  return (
    <>
      <button onClick={share} data-testid="share-btn" className={`btn-pill btn-outline ${className}`}><Share2 size={15} /> Share</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="share-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Share this</DialogTitle></DialogHeader>
          <div className="grid grid-cols-5 gap-2">
            {NETWORKS(url, title).map(({ k, label, icon: Icon, href }) => (
              <a key={k} href={href} target="_blank" rel="noreferrer" data-testid={`share-${k}`}
                className="flex flex-col items-center gap-1.5 rounded-2xl border border-[color:var(--line)] p-3 text-[0.65rem] font-semibold hover:border-[color:var(--coral)] hover:text-[color:var(--coral)] transition-colors">
                <Icon size={18} /> {label}
              </a>
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <input readOnly value={url} data-testid="share-url" className="flex-1 min-w-0 bg-[color:var(--sand)]/40 border border-[color:var(--line)] rounded-xl px-3 py-2 text-xs" onFocus={(e) => e.target.select()} />
            <button onClick={copy} data-testid="share-copy" className="btn-pill btn-primary text-xs">{copied ? <Check size={13} /> : <Copy size={13} />} Copy</button>
          </div>
          <p className="text-[0.65rem] text-[color:var(--ink-2)]">This link shows the event's own photo and title when previewed in messages and social feeds.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
